import React, { useState } from "react";
import { Modal, Row, Col, Button, Input, Select, notification } from "antd";
import axios from "axios";
import QuestionBuilder from "./QuestionBuilder";
import QuestionList from "./QuestionList";
import { AssesmentUrl, BaseUrl } from "../../../Constants/Constants";
import "./ManualAssessment.css";

const ManualAssessmentModal = ({
  visible,
  onCancel,
  CourseData,
  onSuccess,
}) => {
  // Assessment basic info state
  const [assessmentName, setAssessmentName] = useState("");
  const [assessmentLevel, setAssessmentLevel] = useState(null);
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [duration, setDuration] = useState("");
  const [status, setStatus] = useState(true);

  // Manual-specific state
  const [noOfQuestions, setNoOfQuestions] = useState("");

  const [totalQuestions, setTotalQuestions] = useState([]);

  // UI state
  const [btnLoading, setBtnLoading] = useState(false);
  const [api, contextHolder] = notification.useNotification();

  const [editingQuestion, setEditingQuestion] = useState(null);

  const levelOptions = [
    { value: "easy", label: "Easy" },
    { value: "medium", label: "Medium" },
    { value: "hard", label: "Hard" },
  ];

  //   Maps frontend types to backend keys
  const typeToBackendKey = {
    mcq: "mcq",
    tf: "tf",
    fill_blank: "fb",
    fill_blank_desc: "fbd",
    matching_list: "ml",
    multiple_answer: "ma",
    table_q: "table_q",
    image: "img",
  };
  //  Calculate question type counts for backend
  const getQuestionTypeCountsForBackend = () => {
    const counts = {
      mcq: 0,
      tf: 0,
      fb: 0,
      fbd: 0,
      ml: 0,
      ma: 0,
      table_q: 0,
      img: 0,
    };

    totalQuestions.forEach((q) => {
      const backendKey = typeToBackendKey[q.type];
      if (backendKey) {
        counts[backendKey]++;
      }
    });

    return counts;
  };

  // Add this helper function to count questions by type
  const getQuestionTypeCounts = () => {
    const counts = {};
    totalQuestions.forEach((q) => {
      counts[q.type] = (counts[q.type] || 0) + 1;
    });
    return counts;
  };

  // Add this mapping for display labels
  const questionTypeLabels = {
    tf: "True/False",
    mcq: "MCQ",
    multiple_answer: "Multi-Answer",
    fill_blank: "Fill Blanks",
    fill_blank_desc: "Fill Blanks (Desc)",
    matching_list: "Matching",
    table_q: "Table",
    image: "Image",
  };

  // Helper function to generate HTML table from table rows
  // Flexible helper function to generate HTML table for both table_q and matching_list
  const generateTableHTML = (rows, type = "table") => {
    if (!rows || rows.length === 0) return "";

    let tableHTML = "";

    if (type === "table") {
      // Table question: WITH borders
      tableHTML =
        '<table style="width:100%; text-align:center; border-collapse: collapse;" border="1">';
      tableHTML += "<tr><th>Field</th><th>Description</th></tr>";

      rows.forEach((row) => {
        tableHTML += "<tr>";
        tableHTML += `<td>${row.text || ""}</td>`;
        tableHTML += `<td>${row.answer || ""}</td>`;
        tableHTML += "</tr>";
      });
    } else if (type === "matching") {
      // Matching list: NO borders, cleaner layout
      tableHTML =
        '<table style="width:100%; text-align:left; border-collapse: collapse;">';
      tableHTML +=
        '<tr><th style="width:50%; padding:8px;">Column A</th><th style="width:50%; padding:8px;">Column B</th></tr>';

      rows.forEach((row) => {
        tableHTML += "<tr>";
        tableHTML += `<td style="padding:8px;">${row.number}. ${row.left || ""}</td>`;
        tableHTML += `<td style="padding:8px;">${row.label}. ${row.right || ""}</td>`;
        tableHTML += "</tr>";
      });
    }

    tableHTML += "</table>";

    return tableHTML;
  };

  //  Format questions for backend API
  const formatQuestionsForBackend = () => {
    return totalQuestions.map((q) => {
      let formattedQuestion = {
        question_type: typeToBackendKey[q.type] || q.type,
        question: {
          question_text: q.question,
          image_url: [],
        },
        answer: "",
        options: {},
        fbd_description: "",
      };

      // Format based on question type
      switch (q.type) {
        case "tf":
          formattedQuestion.options = {
            option_a: "True",
            option_b: "False",
            option_c: null,
            option_d: null,
          };
          formattedQuestion.answer = q.answer === true ? "True" : "False";
          break;

        case "mcq":
          formattedQuestion.options = {
            option_a: (q.options && q.options[0]) || "",
            option_b: (q.options && q.options[1]) || "",
            option_c: (q.options && q.options[2]) || "",
            option_d: (q.options && q.options[3]) || "",
          };
          formattedQuestion.answer = q.correctAnswers[0] || "";
          break;

        case "multiple_answer":
          formattedQuestion.options = {
            option_a: (q.options && q.options[0]) || "",
            option_b: (q.options && q.options[1]) || "",
            option_c: (q.options && q.options[2]) || "",
            option_d: (q.options && q.options[3]) || "",
          };
          formattedQuestion.answer = q.correctAnswers.join(", ");
          break;

        case "fill_blank":
          formattedQuestion.answer = q.blanks.join(", ");
          break;
        case "fill_blank_desc":
          // Helper function to normalize comma-separated values to pipe-separated
          const normalize = (str) => {
            if (!str) return "";
            return str
              .split(",")
              .map((s) => s.trim())
              .filter((s) => s) // Remove empty strings
              .join("|");
          };

          // Format options from array to object with pipe-separated values
          formattedQuestion.options = {
            option_a: q.options[0] ? normalize(q.options[0]) : "",
            option_b: q.options[1] ? normalize(q.options[1]) : "",
            option_c: q.options[2] ? normalize(q.options[2]) : "",
            option_d: q.options[3] ? normalize(q.options[3]) : "",
          };

          // Format answer
          formattedQuestion.answer = normalize(q.fbdAnswer || "");

          // Add description
          formattedQuestion.fbd_description = q.fbdDescription || "";
          break;
        case "matching_list":
          const mlHTML = generateTableHTML(q.pairs || [], "matching");

          formattedQuestion.question.question_text =
            q.question + "<br>" + mlHTML;
          formattedQuestion.options = {
            option_a: (q.options && q.options[0]) || "",
            option_b: (q.options && q.options[1]) || "",
            option_c: (q.options && q.options[2]) || "",
            option_d: (q.options && q.options[3]) || "",
          };
          formattedQuestion.answer = q.answer || "";

          break;
        case "table_q":
          // Generate HTML table from table_rows
          const tableHTML = generateTableHTML(q.table_rows || [], "table");

          // Combine question text with table HTML
          formattedQuestion.question.question_text =
            q.question + "<br>" + tableHTML;

          // Add options
          formattedQuestion.options = {
            option_a: (q.options && q.options[0]) || "",
            option_b: (q.options && q.options[1]) || "",
            option_c: (q.options && q.options[2]) || "",
            option_d: (q.options && q.options[3]) || "",
          };

          // Add answer
          formattedQuestion.answer = q.answer || "";
          break;

        case "image":
          // Send base64 images
          formattedQuestion.question.image_url = (q.imageData || []).map(
            (img) => {
              const base64String = img.base64.split(",")[1]; // Get part after comma
              return base64String;
            },
          );
          formattedQuestion.options = {
            option_a: (q.options && q.options[0]) || "",
            option_b: (q.options && q.options[1]) || "",
            option_c: (q.options && q.options[2]) || "",
            option_d: (q.options && q.options[3]) || "",
          };
          formattedQuestion.answer = q.answer || "";
          break;
      }

      return formattedQuestion;
    });
  };
  const handleSubmit = async () => {
    // Validation 1: Check basic fields
    if (!assessmentName || !assessmentLevel || !selectedCourseId || !duration) {
      api.warning({
        message: "Missing Fields",
        description:
          "Please fill in all required fields (Name, Level, Course, Duration)",
      });
      return;
    }

    // Validation 2: Check if Total Questions is specified
    if (!noOfQuestions || parseInt(noOfQuestions) <= 0) {
      api.warning({
        message: "Total Questions Required",
        description: "Please specify the total number of questions needed",
      });
      return;
    }

    // Validation 3: Check if any questions added
    if (totalQuestions.length === 0) {
      api.warning({
        message: "No Questions Added",
        description: "Please add at least 1 question before submitting",
      });
      return;
    }

    // Validation 4: Check if question count matches requirement
    const requiredCount = parseInt(noOfQuestions);
    const addedCount = totalQuestions.length;

    if (addedCount < requiredCount) {
      api.warning({
        message: "Insufficient Questions",
        description: `You need to add ${requiredCount - addedCount} more question(s). Required: ${requiredCount}, Added: ${addedCount}`,
      });
      return;
    }

    // If all validations pass, proceed with submission
    setBtnLoading(true);

    try {
      // Step 1: Create assessment
      const assessmentBody = {
        course_id: selectedCourseId,
        Number_of_questions: totalQuestions.length.toString(),
        Assesment_Duration: duration,
        Assessment_name: assessmentName,
        Assessment_level: assessmentLevel,
        status: status,
        assessment_type: "manual",
        assessment_type_id: 3,
        assessment_object: JSON.stringify(totalQuestions),
      };

      const assessmentResponse = await axios.post(
        `${BaseUrl}assesment/add`,
        assessmentBody,
        { withCredentials: true },
      );

      if (assessmentResponse.data.statuscode === 200) {
        const assessmentIdforManual = assessmentResponse.data.data[0].Assesment_id;

        // Get question type counts for backend
        const typeCounts = getQuestionTypeCountsForBackend();

        // Format questions for backend
        const formattedQuestions = formatQuestionsForBackend();

        // Step 2: Add manual questions with proper payload structure
        const questionsBody = {
          course_id: selectedCourseId,
          assessment_name: assessmentName,
          no_of_questions: totalQuestions.length,

          // Question type counts
          mcq: typeCounts.mcq,
          tf: typeCounts.tf,
          fb: typeCounts.fb,
          fbd: typeCounts.fbd,
          ml: typeCounts.ml,
          ma: typeCounts.ma,
          table_q: typeCounts.table_q,
          img: typeCounts.img,

          // Formatted questions array
          questions: formattedQuestions,
          assessment_id : assessmentIdforManual , 
          // Additional fields
          assessment_status: status ? "true" : "false",
          assessment_duration: parseInt(duration),
        };

        const questionsResponse = await axios.post(
          `${AssesmentUrl}add_manual_questions`,
          questionsBody,
          { withCredentials: true },
        );

        if (questionsResponse.data.status === 200) {
          api.success({
            message: "Success!",
            description: `Manual assessment with ${totalQuestions.length} questions created successfully!`,
          });

          // Reset form
          resetForm();

          // Close modal and refresh parent
          onSuccess();
        } else {
          throw new Error("Failed to add questions");
        }
      } else {
        throw new Error("Failed to create assessment");
      }
    } catch (error) {
      console.error("Error creating manual assessment:", error);
      api.error({
        message: "Error",
        description:
          error.message || "Failed to create assessment. Please try again.",
      });
    } finally {
      setBtnLoading(false);
    }
  };

  const resetForm = () => {
    setAssessmentName("");
    setAssessmentLevel(null);
    setSelectedCourseId(null);
    setDuration("");
    setStatus(true);
    setNoOfQuestions("");
    setTotalQuestions([]);
  };

  const handleCancel = () => {
    resetForm();
    onCancel();
  };

  return (
    <>
      {contextHolder}
      <Modal
        title={
          <span style={{ fontSize: "18px", fontWeight: 600 }}>
            Add Assessment - Manual Questions
          </span>
        }
        open={visible}
        onCancel={handleCancel}
        footer={null}
        width={1200}
        className="manualAssessmentModal"
      >
        <Row gutter={16}>
          {/* LEFT COLUMN - Basic Details */}
          <Col span={9}>
            <h3 style={{ marginTop: 0, marginBottom: 16 }}>
              Fill in the details
            </h3>

            <p className="fieldLabel">Assessment Name *</p>
            <Input
              placeholder="Enter assessment name"
              value={assessmentName}
              onChange={(e) => setAssessmentName(e.target.value)}
              className="formInput"
            />

            <p className="fieldLabel">Assessment Level *</p>
            <Select
              value={assessmentLevel}
              placeholder="Select difficulty level"
              className="formInput"
              onChange={setAssessmentLevel}
              options={levelOptions}
              style={{ width: "100%" }}
            />

            <p className="fieldLabel">Select Course *</p>
            <Select
              value={selectedCourseId}
              placeholder="Select course"
              className="formInput"
              onChange={setSelectedCourseId}
              options={CourseData}
              style={{ width: "100%" }}
            />

            <p className="fieldLabel">Assessment Duration (minutes) *</p>
            <Input
              type="number"
              placeholder="e.g., 60"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              className="formInput"
            />

            <p className="fieldLabel">Status</p>
            <Select
              value={status}
              className="formInput"
              onChange={setStatus}
              options={[
                { value: true, label: "Active" },
                { value: false, label: "Inactive" },
              ]}
              style={{ width: "100%" }}
            />
          </Col>

          {/* RIGHT COLUMN - Question Builder */}
          <Col span={14} offset={1}>
            <h3 style={{ marginTop: 0, marginBottom: 16 }}>
              Build Your Questions
            </h3>

            {/* Planning Inputs */}
            {/* Total Questions Input - Full Width */}
            <div style={{ marginBottom: 16 }}>
              <p className="fieldLabel">Total Questions Required *</p>
              <Input
                type="number"
                min="1"
                placeholder="e.g., 10"
                value={noOfQuestions}
                onChange={(e) => setNoOfQuestions(e.target.value)}
                className="formInput"
                style={{ width: "100%" }}
              />
              <p style={{ fontSize: "12px", color: "#888", marginTop: 4 }}>
                Enter the total number of questions needed for this assessment
              </p>
            </div>

            {/* Question Builder Component */}
            <QuestionBuilder
              totalQuestions={totalQuestions}
              setTotalQuestions={setTotalQuestions}
              editingQuestion={editingQuestion}
              onEditComplete={() => setEditingQuestion(null)}
            />
            {totalQuestions.length > 0 && (
              <div
                style={{
                  marginTop: 16,
                  padding: "12px",
                  background: "#f9f9f9",
                  borderRadius: "6px",
                  border: "1px solid #e0e0e0",
                }}
              >
                <p
                  style={{
                    margin: "0 0 8px 0",
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#333",
                  }}
                >
                  📊 Question Type Breakdown:
                </p>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                  }}
                >
                  {Object.entries(getQuestionTypeCounts()).map(
                    ([type, count]) => (
                      <span
                        key={type}
                        style={{
                          display: "inline-block",
                          padding: "4px 12px",
                          background: "#1677ff",
                          color: "#fff",
                          borderRadius: "12px",
                          fontSize: "12px",
                          fontWeight: 500,
                        }}
                      >
                        {questionTypeLabels[type]}: {count}
                      </span>
                    ),
                  )}
                </div>
                <p
                  style={{
                    margin: "8px 0 0 0",
                    fontSize: "12px",
                    color: "#666",
                  }}
                >
                  Total Added: <strong>{totalQuestions.length}</strong> /{" "}
                  {noOfQuestions || "?"} required
                </p>
              </div>
            )}

            {/* Question List Component */}
            <QuestionList
              questions={totalQuestions}
              setQuestions={setTotalQuestions}
              targetCount={noOfQuestions}
              onEditQuestion={setEditingQuestion}
            />
          </Col>
        </Row>

        {/* Footer Buttons */}
        <Row
          className="modalFooter"
          style={{ marginTop: 24, justifyContent: "flex-end", gap: 12 }}
        >
          <Button
            onClick={handleCancel}
            style={{ border: "1px solid #000", marginRight: 12 }}
          >
            Cancel
          </Button>
          <Button type="primary" onClick={handleSubmit} loading={btnLoading}>
            Create Assessment
          </Button>
        </Row>
      </Modal>
    </>
  );
};

export default ManualAssessmentModal;
